import React, { useCallback, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import Select from '../ui/Select/Select'
import TextField from '../EditCollect/BasicFields/TextField'
import ErrorAlert from '../shared/Export/ErrorAlert'
import { Button, RadioGroup } from '../ui'
import { useSummaryForm } from './hooks/useSummaryForm'
import { toSelectOptions, formOptionsPropType } from './constants'
import ChannelFields from './components/ChannelFields'
import RecurrenceField from './components/RecurrenceField'
import ExternalEmailsField from './components/ExternalEmailsField'
import SummaryFilters from './SummaryFilters'

const EXPORT_PERIOD_OPTIONS = [
  { id: 'export_all_false', value: false, label: 'Uniquement sur la période' },
  { id: 'export_all_true', value: true, label: 'Extraction globale' }
]

const SummaryForm = ({
  collectId, summary, formOptions, onSuccess, onCancel
}) => {
  const {
    formData, updateField, handleGeneratorChange,
    handleChannelChange, handleSubmit, submitting, errors,
    availableGenerators, availableRecurrences,
    showSections, showSftpOptions,
    showExternalEmails, showPeriodOnly, showCustomCron, toggleCustomMode,
    showCurrentDraftOnly, showPriorityResources
  } = useSummaryForm({
    collectId, summary, formOptions, onSuccess
  })

  const generatorOptions = useMemo(() => toSelectOptions(availableGenerators), [availableGenerators])
  const sectionOptions = useMemo(() => toSelectOptions(formOptions.sections), [formOptions.sections])
  const variableOptions = useMemo(() => toSelectOptions(formOptions.variables), [formOptions.variables])
  const credentials = formOptions.credentials || []
  const credentialOptions = useMemo(() => toSelectOptions(credentials), [credentials])

  const selectedGenerator = generatorOptions.find(
    option => option.value === formData.generator
  )

  const filtersRef = useRef(null)

  const handleFormSubmit = useCallback(event => {
    event.preventDefault()
    const filtersAttributes = filtersRef.current?.getFilters() || []
    handleSubmit(filtersAttributes)
  }, [handleSubmit])

  const handleNameChange = useCallback(event => {
    updateField('name', event.target.value)
  }, [updateField])

  const handleUsersChange = useCallback(selected => {
    updateField('userIds', selected || [])
  }, [updateField])

  const handleSectionsChange = useCallback(selected => {
    updateField('sectionIds', selected || [])
  }, [updateField])

  const handleExportAllChange = useCallback(value => {
    updateField('exportAll', value)
  }, [updateField])

  const handlePriorityChange = useCallback(event => {
    updateField('priorityResources', event.target.checked)
  }, [updateField])

  const handleDraftOnlyChange = useCallback(event => {
    updateField('currentDraftOnly', event.target.checked)
  }, [updateField])


  const submitLabel = summary? 'Sauvegarder' : 'Créer le récapitulatif'

  return (
    <form onSubmit={handleFormSubmit}>
      {errors.base ?<ErrorAlert message={errors.base.join(', ')} />: null}
      <TextField
        name="summary-name"
        label="Nom"
        value={formData.name}
        errors={errors.name}
        onChange={handleNameChange}
      />
      {formOptions.channels.length > 1 ? (
        <ChannelFields
          availableChannels={formOptions.channels}
          channel={formData.channel}
          credentialId={formData.credentialId}
          credentialOptions={credentialOptions}
          credentialError={errors.credential}
          outputFormat={formData.outputFormat}
          outputFormatError={errors.output_format}
          showSftpOptions={showSftpOptions}
          onChannelChange={handleChannelChange}
          onFieldChange={updateField}
        />
      ) : null}
      <div className="form-group">
        <label>Destinataires du récapitulatif</label>
        <Select
          options={formOptions.users}
          value={formData.userIds}
          isMulti
          isSearchable
          showSelectAll
          onChange={handleUsersChange}
        />
      </div>
      <div className="form-group">
        <label>Type de récapitulatif</label>
        <Select
          options={generatorOptions}
          value={selectedGenerator}
          error={Boolean(errors.generator)}
          onChange={handleGeneratorChange}
        />
        {errors.generator ?<small className="text-danger">{errors.generator[0]}</small>: null}
      </div>
      {showSections ? (
        <div className="form-group">
          <label>Sections</label>
          <Select
            options={sectionOptions}
            value={formData.sectionIds}
            error={Boolean(errors.sections)}
            isMulti
            isSearchable
            showSelectAll
            onChange={handleSectionsChange}
          />
          {errors.sections ?<small className="text-danger">{errors.sections[0]}</small>: null}
        </div>
      ) : null}
      <RecurrenceField
        cronExpression={formData.cronExpression}
        error={errors.cron_expression}
        recurrenceOptions={availableRecurrences}
        showCustomCron={showCustomCron}
        onFieldChange={updateField}
        onToggleCustom={toggleCustomMode}
      />
      {showPeriodOnly ? (
        <RadioGroup
          name="exportAll"
          label="Période d'exportation"
          className='mt-3'
          options={EXPORT_PERIOD_OPTIONS}
          value={formData.exportAll}
          inline
          onChange={handleExportAllChange}
        />
      ) : null}
      {showExternalEmails ? (
        <ExternalEmailsField
          useExternalEmails={formData.useExternalEmails}
          variableId={formData.variableId}
          variableOptions={variableOptions}
          variableError={errors.variable}
          onFieldChange={updateField}
        />
      ) : null}
      <SummaryFilters
        ref={filtersRef}
        collectId={collectId}
        currentDraftOnly={formData.currentDraftOnly}
        errors={errors}
        initialFilters={formData.summaryFilters}
        priorityResources={formData.priorityResources}
        showCurrentDraftOnly={showCurrentDraftOnly}
        showPriorityResources={showPriorityResources}
        variables={formOptions.variables}
        onDraftOnlyChange={handleDraftOnlyChange}
        onPriorityChange={handlePriorityChange}
      />
      <div className="d-flex flex-column flex-sm-row mt-3">
        <Button
          type="submit"
          className="order-sm-last ml-sm-auto"
          disabled={submitting}
        >
          {submitting ? 'Patientez...' : submitLabel}
        </Button>
        {onCancel ? (
          <Button
            variant="outline"
            className="mt-2 mt-sm-0"
            onClick={onCancel}
          >
            Fermer
          </Button>
        ) : null}
      </div>
    </form>
  )
}

SummaryForm.propTypes = {
  collectId: PropTypes.number.isRequired,
  formOptions: formOptionsPropType.isRequired,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func.isRequired,
  summary: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string
  })
}

SummaryForm.defaultProps = {
  onCancel: null,
  summary: null
}

export default SummaryForm
